import { useMemo } from "react";
import { Link, NavLink, Outlet, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api, type RangeParams, type Site } from "../lib/api";
import { buildRange, RANGE_OPTIONS, type RangeKind } from "../lib/format";
import { AppShell } from "../components/AppShell";
import { RangePicker } from "../components/RangePicker";
import { ErrorBox, PageSpinner } from "../components/ui";

export interface SiteOutletContext {
  site: Site;
  range: RangeParams;
  rangeKind: RangeKind;
  setRangeKind: (kind: RangeKind) => void;
}

const TABS = [
  { to: "", label: "Ringkasan", end: true },
  { to: "realtime", label: "Realtime", end: false },
  { to: "events", label: "Peristiwa", end: false },
  { to: "settings", label: "Pengaturan", end: false },
];

export default function SiteLayout() {
  const { siteId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const siteQuery = useQuery({
    queryKey: ["site", siteId],
    queryFn: () => api.get<{ site: Site }>(`/api/sites/${siteId}`),
  });

  const rawKind = searchParams.get("range");
  const rangeKind: RangeKind = RANGE_OPTIONS.some((option) => option.kind === rawKind)
    ? (rawKind as RangeKind)
    : "24h";

  function setRangeKind(kind: RangeKind) {
    const next = new URLSearchParams(searchParams);
    next.set("range", kind);
    setSearchParams(next, { replace: true });
  }

  const site = siteQuery.data?.site;
  const nowRounded = Math.floor(Date.now() / 60000) * 60000;
  const range = useMemo(
    () => buildRange(rangeKind, nowRounded, site?.tz_offset ?? 0),
    [rangeKind, nowRounded, site?.tz_offset],
  );

  if (siteQuery.isLoading) {
    return (
      <AppShell>
        <PageSpinner />
      </AppShell>
    );
  }

  if (!site) {
    return (
      <AppShell>
        <ErrorBox message="Situs tidak ditemukan." />
      </AppShell>
    );
  }

  const context: SiteOutletContext = { site, range, rangeKind, setRangeKind };

  return (
    <AppShell right={<RangePicker kind={rangeKind} onChange={setRangeKind} />}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="btn px-2" title="Kembali">
              <ArrowLeft size={15} />
            </Link>
            <div>
              <h1 className="text-base font-semibold text-slate-900">{site.name}</h1>
              <p className="text-xs text-slate-500">{site.domain ?? "Domain belum diatur"}</p>
            </div>
          </div>
        </div>

        <nav className="flex gap-1 border-b border-slate-200">
          {TABS.map((tab) => (
            <NavLink
              key={tab.label}
              to={tab.to === "" ? `/s/${site.id}` : `/s/${site.id}/${tab.to}`}
              end={tab.end}
              className={({ isActive }) =>
                isActive
                  ? "-mb-px border-b-2 border-emerald-600 px-3 py-2 text-sm font-medium text-slate-900"
                  : "-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium text-slate-500 transition hover:text-slate-800"
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>

        <Outlet context={context} />
      </div>
    </AppShell>
  );
}
